// EditorsToolkit — a permanent slide-out drawer of world-class expert
// content available inside every edition (PR, branding, marketing,
// sales, social media). Universal value baked in.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Briefcase, Copy, Check, X, Filter } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EditorsToolkit({ open, onClose, isLightMode }) {
  const [cards, setCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [copiedSlug, setCopiedSlug] = useState(null);

  useEffect(() => {
    if (!open || cards.length > 0) return;
    axios.get(`${API}/api/library/toolkit`, { withCredentials: true })
      .then((r) => {
        setCards(r.data.cards || []);
        setCategories(["All", ...(r.data.categories || [])]);
      })
      .catch(() => {});
  }, [open, cards.length]);

  const filtered = useMemo(() =>
    active === "All" ? cards : cards.filter((c) => c.category === active),
    [cards, active]
  );

  const copy = (card) => {
    try {
      navigator.clipboard.writeText(card.body);
      setCopiedSlug(card.slug);
      toast.success("Copied. Paste into your draft.");
      setTimeout(() => setCopiedSlug(null), 2000);
    } catch {
      toast.error("Couldn't copy automatically. Select & copy manually.");
    }
  };

  if (!open) return null;

  const bg = isLightMode ? "bg-cream text-ink" : "bg-[#111] text-cream";
  const panelB = isLightMode ? "border-ink/15" : "border-cream/15";
  const tabActive = isLightMode ? "bg-ink text-cream" : "bg-cream text-ink";
  const tabIdle = isLightMode ? "border border-ink/20 hover:border-ink/50" : "border border-cream/20 hover:border-cream/50";
  const card = isLightMode ? "bg-white border-ink/10" : "bg-[#1a1a1c] border-cream/10";
  const muted = isLightMode ? "text-ink/60" : "text-cream/60";

  return (
    <div className="fixed inset-0 z-[55] flex" data-testid="editors-toolkit">
      <button
        className="flex-1 bg-ink/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close toolkit"
        data-testid="toolkit-backdrop"
      />
      <aside className={`${bg} w-full max-w-2xl flex flex-col shadow-2xl border-l ${panelB}`}>
        {/* Header */}
        <div className={`px-6 py-5 border-b ${panelB} flex items-start justify-between gap-4`}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="h-4 w-4 opacity-80"/>
              <p className="overline text-[10px] tracking-[0.3em] opacity-70">The Editor&apos;s Toolkit</p>
            </div>
            <h3 className="font-display text-3xl leading-none">
              Templates, scripts &amp; <span className="font-serif italic">cheats</span>.
            </h3>
            <p className={`text-[12px] mt-2 ${muted} max-w-md leading-relaxed`}>
              Copy-paste-ready PR pitches, post hooks, brand-voice worksheets, hashtag systems, and launch runways. Universal expert content — yours for every edition you own.
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${muted} hover:opacity-100 opacity-70`}
            aria-label="Close"
            data-testid="toolkit-close"
          >
            <X className="h-5 w-5"/>
          </button>
        </div>

        {/* Category tabs */}
        <div className={`px-6 py-3 border-b ${panelB} flex items-center gap-2 overflow-x-auto`}>
          <Filter className="h-3.5 w-3.5 opacity-50 flex-shrink-0"/>
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`toolkit-tab-${c.toLowerCase()}`}
              className={`overline text-[10px] tracking-[0.22em] px-3 py-1.5 whitespace-nowrap transition-colors ${active === c ? tabActive : tabIdle}`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {filtered.map((c) => (
            <article
              key={c.slug}
              className={`${card} border p-5`}
              data-testid={`toolkit-card-${c.slug}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="overline text-[9px] tracking-[0.28em] opacity-60 mb-1">{c.category}</p>
                  <h4 className="font-serif text-xl leading-tight">{c.title}</h4>
                </div>
                <button
                  onClick={() => copy(c)}
                  data-testid={`toolkit-copy-${c.slug}`}
                  className={`flex-shrink-0 inline-flex items-center gap-1.5 overline text-[10px] tracking-[0.22em] px-3 py-2 ${copiedSlug === c.slug ? "bg-emerald-600 text-white" : (isLightMode ? "bg-ink text-cream" : "bg-cream text-ink")} transition-colors`}
                >
                  {copiedSlug === c.slug ? <Check className="h-3 w-3"/> : <Copy className="h-3 w-3"/>}
                  {copiedSlug === c.slug ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className={`whitespace-pre-wrap font-serif text-[13.5px] leading-relaxed ${isLightMode ? "text-ink/85" : "text-cream/85"}`}>
                {c.body}
              </pre>
              {c.why && (
                <p className={`mt-3 pt-3 border-t border-current/10 text-[11px] font-serif italic ${muted}`}>
                  Why it works — {c.why}
                </p>
              )}
            </article>
          ))}
        </div>
      </aside>
    </div>
  );
}
